import PublishIcon from '@mui/icons-material/Publish';
import { Box, Typography } from '@mui/material';
import { checkSIIDocumentForUploadingInvoices } from 'lib/api/sii';
import { showAlert } from 'lib/functions/utils';
import React, { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  applyRuleToRecords,
  type ParseRule,
  readFileToRecords,
} from './parser';
import {
  downloadTemplate,
  getTemplateFilename,
  getTemplateRows,
} from './templates';
import type { DocumentImporterConfig, ImporterDocumentRow } from './types';
import { toDdMmYyyy } from './utils';

const ACCEPT = '.xlsx,.xls,.csv';

function buildFallbackRule(config: DocumentImporterConfig): ParseRule {
  const mapping = config.excelColumnMapping ?? {};
  return {
    columnMapping: mapping,
    dateFields: ['issuedDate', 'dueDate'],
    amountFields: ['amount'],
    requiredKeysForRow: ['folio', 'stakeholderIdentifier'],
  };
}

export interface ExcelUploadProps {
  config: DocumentImporterConfig;
  businessIdentifier: string;
  onAddRows: (rows: ImporterDocumentRow[]) => void;
}

const ExcelUpload: React.FC<ExcelUploadProps> = ({
  config,
  businessIdentifier,
  onAddRows,
}) => {
  const dispatch = useDispatch();
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);

  const parseFile = useCallback(
    async (file: File): Promise<ImporterDocumentRow[]> => {
      const records = await readFileToRecords(file);
      const rule = config.parseRule ?? buildFallbackRule(config);
      return applyRuleToRecords(records, rule, { businessIdentifier });
    },
    [businessIdentifier, config],
  );

  const handleFile = useCallback(
    async (file: File | null) => {
      if (!file) return;
      const name = file.name.toLowerCase();
      if (
        !name.endsWith('.xlsx') &&
        !name.endsWith('.xls') &&
        !name.endsWith('.csv')
      ) {
        showAlert(
          dispatch,
          'error',
          'Formato no soportado. Usa archivos .xlsx, .xls o .csv',
        );
        return;
      }
      setLoading(true);
      try {
        const rows = await parseFile(file);
        if (rows.length === 0) {
          showAlert(
            dispatch,
            'warning',
            'No se encontraron filas válidas en el archivo',
          );
        } else if (config.validateWithSII) {
          const invoicesForSII = rows.map((row) => ({
            ...row,
            issuedDate: toDdMmYyyy(row.issuedDate),
          }));
          const response = await checkSIIDocumentForUploadingInvoices(
            dispatch,
            { invoices: invoicesForSII },
          );
          const validFolios =
            response
              ?.filter((r: { valid?: boolean }) => r.valid)
              .map((r: { folio: string }) => r.folio) ?? [];
          const validRows = rows.filter((row) =>
            validFolios.includes(row.folio),
          );
          onAddRows(validRows);
          if (validRows.length < rows.length) {
            const rejected = rows.filter((r) => !validFolios.includes(r.folio));
            showAlert(
              dispatch,
              'warning',
              `Los siguientes documentos no pasaron la validación SII: ${rejected.map((r) => r.folio).join(', ')}`,
            );
          }
        } else {
          onAddRows(rows);
        }
      } catch (err) {
        console.error(err);
        showAlert(dispatch, 'error', 'Error al procesar el archivo');
      } finally {
        setLoading(false);
      }
    },
    [
      config.validateWithSII,
      config.parseRule,
      config.excelColumnMapping,
      dispatch,
      parseFile,
      onAddRows,
    ],
  );

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      void handleFile(file ?? null);
    },
    [handleFile],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const onDragLeave = useCallback(() => {
    setDragOver(false);
  }, []);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      void handleFile(file ?? null);
      e.target.value = '';
    },
    [handleFile],
  );

  const handleDownloadTemplate = useCallback(() => {
    const key = config.templateKey ?? 'document';
    const rows = getTemplateRows(key);
    const filename = getTemplateFilename(key);
    downloadTemplate(rows, filename);
  }, [config.templateKey]);

  return (
    <div>
      <Box
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        sx={{
          border: '2px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          borderRadius: 2,
          p: 4,
          textAlign: 'center',
          bgcolor: dragOver ? 'action.hover' : 'transparent',
          cursor: 'pointer',
        }}
        onClick={() => document.getElementById('excel-file-input')?.click()}
      >
        <input
          id="excel-file-input"
          type="file"
          accept={ACCEPT}
          onChange={onInputChange}
          style={{ display: 'none' }}
          disabled={loading}
        />
        {loading ? (
          <Typography color="text.secondary">Procesando archivo...</Typography>
        ) : (
          <>
            <PublishIcon
              sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
            />
            <Typography variant="body1" display="block">
              Suelta tu archivo Excel o CSV aquí o{' '}
              <Typography component="span" sx={{ textDecoration: 'underline' }}>
                explora tus archivos
              </Typography>
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.5 }}
            >
              Compatible con archivos .xlsx, .xls y .csv
            </Typography>
          </>
        )}
      </Box>
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
      >
        <Typography
          variant="caption"
          display="block"
          sx={{
            mt: 1,
            cursor: 'pointer',
            color: 'primary.main',
            fontWeight: 600,
            fontSize: 'body2',
            '&:hover': { textDecoration: 'underline' },
          }}
          onClick={handleDownloadTemplate}
        >
          Descargar template
        </Typography>
      </Box>
    </div>
  );
};

export default ExcelUpload;
